'use client'

import * as React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons/faX'
import { useAppDispatch, useAppSelector } from '@/lib/store'
import { getSizes } from '@/lib/features/sizes/sizesSlice'
import { EnumOrientationType, EnumSizeType } from '../../../../../../../../../../lib/database/lib/generated/enums'
import { getBrowseFiltersAspectRatio, getBrowseFiltersSize } from '@/lib/features/browseFilters/browseFiltersSlice'

export default function BrowsePageSizeMenu(): React.ReactElement {
  const dispatch = useAppDispatch()
  const sizes = useAppSelector(getSizes)
  const selectedSize = useAppSelector(getBrowseFiltersSize)
  const selectedAspectRatio = useAppSelector(getBrowseFiltersAspectRatio)

  const [isMenuOpen, setIsMenuOpen] = React.useState<boolean>(false)

  const menuRef = React.useRef<HTMLDivElement | null>(null)
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null)

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (isMenuOpen) {
        if (
          menuRef.current &&
          !menuRef.current.contains(event.target as Node) &&
          menuButtonRef.current &&
          !menuButtonRef.current.contains(event.target as Node)
        ) {
          setIsMenuOpen(false)
        }
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen, menuRef])

  return (
    <div className="relative flex flex-col">
      <label htmlFor="size" className="text-primary-text dark:text-primary-text-dark block">
        <button
          ref={menuButtonRef}
          id="size"
          className={`button-small ${selectedSize ? 'button-default-outline' : 'button-default-outline-faded'} inline-flex h-[30px] gap-0 p-0`}
          type="button"
          onClick={() => {
            setIsMenuOpen(!isMenuOpen)
          }}
        >
          <div className={`ml-2.5 ${!selectedSize ? 'mr-2.5' : ''}`}>
            <span>{`Size: ${selectedSize ? `${selectedSize.width}x${selectedSize.height} (${selectedSize.aspect_ratio})` : 'Any'}`}</span>
            {selectedSize?.type === 'MEDIUM' && (
              <svg
                className="mb-1 ms-2 inline size-2.5 translate-y-px text-yellow-300"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="currentColor"
                viewBox="0 0 22 20"
              >
                <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
              </svg>
            )}
            {selectedSize?.type === 'LARGE' && (
              <>
                <svg
                  className="mb-1 ms-2 inline size-2.5 translate-y-px text-yellow-300"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 22 20"
                >
                  <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                </svg>
                <svg
                  className="mb-1 ms-1 inline size-2.5 translate-y-px text-yellow-300"
                  aria-hidden="true"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 22 20"
                >
                  <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                </svg>
              </>
            )}
          </div>
          {selectedSize && (
            <FontAwesomeIcon
              icon={faX}
              className="ml-2 size-2.5 rounded-r-md p-2.5 hover:bg-neutral-500/5 dark:hover:hover:bg-neutral-500/25"
              onClick={(event: React.MouseEvent<SVGSVGElement>) => {
                void (async () => {
                  event.preventDefault()
                  event.stopPropagation()

                  const { setBrowseFiltersPage, setBrowseFiltersSize } = await import('@/lib/features/browseFilters/browseFiltersSlice')

                  dispatch(setBrowseFiltersSize(null))
                  dispatch(setBrowseFiltersPage(1))
                })()
              }}
            />
          )}
        </button>
      </label>
      <div
        ref={menuRef}
        className={`z-10 ${!isMenuOpen ? 'hidden' : ''} shadow-shadow dark:shadow-shadow-dark absolute top-[40px] w-64 divide-y divide-neutral-100 rounded-lg bg-white shadow-lg dark:bg-neutral-700`}
      >
        <ul
          className="text-secondary-text dark:text-secondary-text-dark max-h-64 overflow-y-auto py-2 text-sm"
          aria-labelledby="dropdownDefaultButton"
        >
          {[EnumSizeType.LARGE, EnumSizeType.MEDIUM, EnumSizeType.SMALL].map((sizeType) => (
            <div key={`sizeType_${sizeType}`}>
              {sizeType === EnumSizeType.LARGE && (
                <li className="text-disabled-text dark:text-secondary-text-dark mx-2 mb-2 block cursor-default rounded-lg bg-neutral-100 px-4 pb-2 pt-3 text-xs font-bold dark:bg-neutral-800">
                  <span>Large (~2.0 Megapixels)</span>
                  <svg
                    className="mb-1 ms-3 inline size-4 text-yellow-300"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 22 20"
                  >
                    <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                  </svg>
                  <svg
                    className="mb-1 ms-1 inline size-4 text-yellow-300"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 22 20"
                  >
                    <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                  </svg>
                </li>
              )}
              {sizeType === EnumSizeType.MEDIUM && (
                <li className="text-disabled-text dark:text-secondary-text-dark m-2 block cursor-default rounded-lg bg-neutral-100 px-4 pb-2 pt-3 text-xs font-bold dark:bg-neutral-800">
                  <span>Medium (~1.0 Megapixels)</span>
                  <svg
                    className="mb-1 ms-3 inline size-4 text-yellow-300"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 22 20"
                  >
                    <path d="M20.924 7.625a1.523 1.523 0 0 0-1.238-1.044l-5.051-.734-2.259-4.577a1.534 1.534 0 0 0-2.752 0L7.365 5.847l-5.051.734A1.535 1.535 0 0 0 1.463 9.2l3.656 3.563-.863 5.031a1.532 1.532 0 0 0 2.226 1.616L11 17.033l4.518 2.375a1.534 1.534 0 0 0 2.226-1.617l-.863-5.03L20.537 9.2a1.523 1.523 0 0 0 .387-1.575Z" />
                  </svg>
                </li>
              )}
              {sizeType === EnumSizeType.SMALL && (
                <li className="text-disabled-text dark:text-secondary-text-dark m-2 block cursor-default rounded-lg bg-neutral-100 px-4 pb-2 pt-3 text-xs font-bold dark:bg-neutral-800">
                  <span>Small (~0.25 Megapixels)</span>
                </li>
              )}
              {sizes
                ?.filter((size) => size.type === sizeType && (!selectedAspectRatio || selectedAspectRatio === size.aspect_ratio))
                .sort((sizeA, sizeB) => {
                  if (sizeA.orientation_type === EnumOrientationType.SQUARE && sizeB.orientation_type !== EnumOrientationType.SQUARE) {
                    return -1
                  }

                  if (sizeA.orientation_type !== EnumOrientationType.SQUARE && sizeB.orientation_type === EnumOrientationType.SQUARE) {
                    return 1
                  }

                  if (sizeA.orientation_type === EnumOrientationType.LANDSCAPE && sizeB.orientation_type === EnumOrientationType.PORTRAIT) {
                    return -1
                  }

                  if (sizeA.orientation_type === EnumOrientationType.PORTRAIT && sizeB.orientation_type === EnumOrientationType.LANDSCAPE) {
                    return 1
                  }

                  return sizeA.orientation_type === EnumOrientationType.PORTRAIT ? sizeA.height - sizeB.height : sizeA.width - sizeB.width
                })
                .map((size) => (
                  <li
                    key={`size_${size.code}`}
                    role="presentation"
                    className="flex cursor-pointer justify-between hover:bg-neutral-100 dark:hover:bg-neutral-600"
                    onClick={() => {
                      void (async () => {
                        const { setBrowseFiltersAspectRatio, setBrowseFiltersPage, setBrowseFiltersSize } = await import(
                          '@/lib/features/browseFilters/browseFiltersSlice'
                        )

                        dispatch(setBrowseFiltersSize(size))
                        dispatch(setBrowseFiltersAspectRatio(null))
                        dispatch(setBrowseFiltersPage(1))
                        setIsMenuOpen(false)
                      })()
                    }}
                  >
                    <div className="block px-4 py-2">
                      <span>{`${size.width}x${size.height}`}</span>
                    </div>
                    <div className="block px-4 py-2">
                      <span>{`(${size.aspect_ratio})`}</span>
                    </div>
                  </li>
                ))}
            </div>
          ))}
        </ul>
      </div>
    </div>
  )
}
